// Drag-and-drop file target. Dropping files onto it or clicking it (which opens
// the native picker) both end in one `onFiles(File[])` call.
//
// Lifted out of ContentIQSetupWizard (where it was a local DropZone, Edward
// 2026-08-25) so the Programs wizard could take a dropped IO PDF without a second
// copy. Defaults keep ContentIQ's look; pass `colors` to fit another surface.
import { useRef, useState } from 'react';
import { BRAND, HUES, INK, NEUTRAL, RADII } from '../utils/wizardTheme';

const DEFAULT_COLORS = {
  accent: BRAND.button,          // border while a drag hovers
  border: NEUTRAL.control,
  tint: HUES.strategy.tint,      // background while a drag hovers
  band: NEUTRAL.band,
  text: INK.body,
  muted: INK.muted,
  radius: RADII.tile,
};

// `accept="application/pdf"` only filters the picker; a drag-and-drop lands
// whatever was dragged, so callers that want PDFs check the dropped file too.
export function isPdfFile(file) {
  if (!file) return false;
  if (file.type === 'application/pdf') return true;
  return /\.pdf$/i.test(file.name || '');
}

export default function FileDropZone({
  label, hint, onFiles, disabled, inputRef, accept,
  multiple = true, colors, style, children, ariaLabel,
}) {
  const [over, setOver] = useState(false);
  const ownRef = useRef(null);
  const ref = inputRef || ownRef;
  const c = { ...DEFAULT_COLORS, ...(colors || {}) };
  const deliver = (list) => {
    const files = [...(list || [])];
    if (!disabled && files.length) onFiles(multiple ? files : files.slice(0, 1));
  };
  return (
    <div
      role="button"
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      onClick={() => !disabled && ref.current && ref.current.click()}
      onKeyDown={e => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); ref.current?.click(); } }}
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); deliver(e.dataTransfer.files); }}
      style={{
        border: `2px dashed ${over ? c.accent : c.border}`,
        background: over ? c.tint : c.band,
        borderRadius: c.radius, padding: '16px', textAlign: 'center',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
        ...(style || {}),
      }}>
      {children || (
        <>
          <div style={{ fontSize: '13px', fontWeight: 600, color: c.text }}>{label}</div>
          {hint && <div style={{ fontSize: '11.5px', color: c.muted, marginTop: '2px' }}>{hint}</div>}
        </>
      )}
      <input ref={ref} type="file" multiple={multiple} style={{ display: 'none' }} accept={accept}
        onChange={e => { const fs = e.target.files; const list = [...(fs || [])]; e.target.value = ''; deliver(list); }} />
    </div>
  );
}
